import { useEffect, useRef, useState } from 'react';
import {
  MagnifyingGlassIcon,
  XMarkIcon,
  ExclamationTriangleIcon,
  ShoppingBagIcon,
  ChevronDownIcon,
  SparklesIcon,
} from '@heroicons/react/24/outline';
import ProductCard from '../components/ProductCard';
import Pagination from '../components/Pagination';
import Reveal from '../components/Reveal';
import { ProductGridSkeleton } from '../components/Skeletons';
import useProducts from '../hooks/useProducts';
import useDebouncedValue from '../hooks/useDebouncedValue';

const PAGE_SIZES = [12, 24, 48];

export default function Icons() {
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(PAGE_SIZES[0]);
  const [searchInput, setSearchInput] = useState('');
  const search = useDebouncedValue(searchInput, 400);
  const topRef = useRef(null);

  const { products, total, loading, error, reload } = useProducts({ page, limit, search });
  const totalPages = Math.max(1, Math.ceil(total / limit));

  // New search / page size → back to the first page
  useEffect(() => {
    setPage(1);
  }, [search, limit]);

  // Keep the page inside valid bounds when the result set shrinks
  useEffect(() => {
    if (page > totalPages) setPage(totalPages);
  }, [page, totalPages]);

  const scrollToTop = () => {
    const element = topRef.current;
    if (!element) return;
    const top = element.getBoundingClientRect().top + window.scrollY - 96;
    window.scrollTo({ top, behavior: 'smooth' });
  };

  const handlePageChange = (nextPage) => {
    setPage(nextPage);
    scrollToTop();
  };

  const clearSearch = () => {
    setSearchInput('');
    setPage(1);
  };

  const isSearching = search.trim().length > 0;

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Page header */}
      <section className="relative overflow-hidden border-b border-slate-200/70 bg-white">
        <div className="pointer-events-none absolute inset-0 bg-soft-gradient" aria-hidden="true" />
        <div className="pointer-events-none absolute -right-20 -top-24 h-64 w-64 animate-blob rounded-full bg-brand-200/50 blur-3xl" aria-hidden="true" />

        <div ref={topRef} className="container-page relative py-10 sm:py-14">
          <Reveal>
            <span className="chip border border-brand-200/70 bg-brand-50 text-brand-700">
              <SparklesIcon className="h-3.5 w-3.5" />
              Katalog produk
            </span>
            <h1 className="mt-4 text-3xl font-extrabold text-slate-900 sm:text-4xl">
              Temukan produk favoritmu
            </h1>
            <p className="mt-2 max-w-2xl text-sm leading-6 text-slate-600 sm:text-base">
              {isSearching ? (
                <>
                  Hasil pencarian untuk{' '}
                  <span className="font-semibold text-slate-900">“{search}”</span>
                </>
              ) : (
                'Cari, telusuri, dan buka detail produk. Semua data dimuat langsung dari API.'
              )}
            </p>
          </Reveal>
        </div>
      </section>

      <section className="container-page py-8 sm:py-10">
        {/* Toolbar */}
        <div className="mb-6 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div className="relative w-full sm:max-w-md">
            <MagnifyingGlassIcon className="pointer-events-none absolute left-4 top-1/2 h-5 w-5 -translate-y-1/2 text-slate-400" />
            <input
              type="search"
              value={searchInput}
              onChange={(event) => setSearchInput(event.target.value)}
              placeholder="Cari produk, misalnya “phone”…"
              aria-label="Cari produk"
              className="input-base !py-3 pl-11 pr-11"
            />
            {searchInput && (
              <button
                type="button"
                onClick={clearSearch}
                aria-label="Hapus pencarian"
                className="absolute right-3 top-1/2 flex h-7 w-7 -translate-y-1/2 items-center justify-center rounded-full text-slate-400 transition hover:bg-slate-100 hover:text-slate-700"
              >
                <XMarkIcon className="h-4 w-4" strokeWidth={2.5} />
              </button>
            )}
          </div>

          <div className="flex items-center justify-between gap-3 sm:justify-end">
            <span className="text-sm text-slate-500">
              {loading ? (
                <span className="inline-flex items-center gap-2">
                  <span className="h-2 w-2 animate-ping rounded-full bg-brand-500" />
                  Memuat…
                </span>
              ) : (
                <>
                  <span className="font-semibold text-slate-800">{total}</span> produk
                </>
              )}
            </span>

            <label className="relative inline-flex items-center">
              <span className="sr-only">Produk per halaman</span>
              <select
                value={limit}
                onChange={(event) => setLimit(Number(event.target.value))}
                className="input-base appearance-none !py-2.5 pl-3.5 pr-9 text-sm font-semibold"
              >
                {PAGE_SIZES.map((size) => (
                  <option key={size} value={size}>
                    {size} / halaman
                  </option>
                ))}
              </select>
              <ChevronDownIcon className="pointer-events-none absolute right-3 h-4 w-4 text-slate-400" />
            </label>
          </div>
        </div>

        {/* Content */}
        {error ? (
          <div className="card-surface flex flex-col items-center justify-center gap-4 px-6 py-20 text-center">
            <span className="flex h-14 w-14 items-center justify-center rounded-2xl bg-rose-50 text-rose-500">
              <ExclamationTriangleIcon className="h-7 w-7" />
            </span>
            <div>
              <h2 className="text-lg font-bold text-slate-900">Gagal memuat produk</h2>
              <p className="mt-1 max-w-sm text-sm text-slate-600">{error}</p>
            </div>
            <button type="button" onClick={reload} className="btn-primary">
              Coba lagi
            </button>
          </div>
        ) : loading && products.length === 0 ? (
          <ProductGridSkeleton count={limit} />
        ) : products.length === 0 ? (
          <div className="card-surface flex flex-col items-center justify-center gap-4 px-6 py-20 text-center">
            <span className="flex h-14 w-14 items-center justify-center rounded-2xl bg-slate-100 text-slate-400">
              <ShoppingBagIcon className="h-7 w-7" />
            </span>
            <div>
              <h2 className="text-lg font-bold text-slate-900">Produk tidak ditemukan</h2>
              <p className="mt-1 max-w-sm text-sm text-slate-600">
                {isSearching
                  ? `Tidak ada hasil untuk “${search}”. Coba kata kunci lain.`
                  : 'Belum ada produk yang bisa ditampilkan.'}
              </p>
            </div>
            {isSearching && (
              <button type="button" onClick={clearSearch} className="btn-secondary">
                Hapus pencarian
              </button>
            )}
          </div>
        ) : (
          <>
            <div
              className={`grid grid-cols-1 gap-5 transition-opacity duration-300 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 ${
                loading ? 'pointer-events-none opacity-50' : ''
              }`}
            >
              {products.map((product, index) => (
                <ProductCard key={product.id} product={product} index={index} />
              ))}
            </div>

            <div className="mt-10">
              <Pagination
                page={page}
                totalPages={totalPages}
                totalItems={total}
                limit={limit}
                onChange={handlePageChange}
                loading={loading}
              />
            </div>
          </>
        )}
      </section>
    </div>
  );
}
